import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, ChevronRight, CheckCircle2, MapPin, Search, Star, Building, Phone, Globe, Image, MessageSquare, BarChart3 } from 'lucide-react';

const LocalSEO = () => {
  const services = [
    {
      title: 'Google Business Profile Setup',
      icon: Building,
      items: ['Complete profile creation', 'Business verification', 'Category optimization', 'Service area setup']
    },
    {
      title: 'Profile Optimization',
      icon: Search,
      items: ['Keyword-rich descriptions', 'Photo & video uploads', 'Product/service listings', 'Attribute optimization']
    },
    {
      title: 'Review Management',
      icon: Star,
      items: ['Review monitoring', 'Response management', 'Review generation strategies', 'Reputation building']
    },
    {
      title: 'Local SEO',
      icon: MapPin,
      items: ['Local keyword research', 'Citation building', 'NAP consistency', 'Local link building']
    }
  ];

  const benefits = [
    'Appear in Google Maps searches',
    'Show up in "near me" queries',
    'Display business hours & contact info',
    'Showcase customer reviews',
    'Post updates and offers',
    'Track customer interactions'
  ];

  const whyChooseUs = [
    { icon: BarChart3, title: 'Data-Driven Approach', description: 'Analytics-based optimization strategies' },
    { icon: Globe, title: 'Local Expertise', description: 'Understanding of Indian local search landscape' },
    { icon: MessageSquare, title: 'Review Management', description: 'Proactive reputation management' },
    { icon: Image, title: 'Visual Optimization', description: 'Professional photo & video guidance' }
  ];

  return (
    <div className="min-h-screen pt-32">
      <Helmet>
        <title>Local SEO & Google Business Optimization | Obiquiste Solutions</title>
        <meta name="description" content="Improve your local visibility with Google Business optimization & local SEO. Get found by customers in your area. Professional local search services India." />
        <link rel="canonical" href="https://www.obiquistesolutions.com/google-business-profile-optimization" />
      </Helmet>
      {/* Breadcrumb */}
      <div className="bg-slate-50 py-4">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="flex items-center text-sm text-slate-600">
            <Link to="/" className="hover:text-primary">Home</Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <Link to="/services" className="hover:text-primary">Services</Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <span className="text-primary font-medium">Local SEO & Google Business</span>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-blue-50 to-green-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <MapPin className="w-4 h-4 mr-2" />
                Local Search Optimization
              </div>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
                Local SEO & <span className="text-green-600">Google Business</span> Optimization
              </h1>
              <p className="text-lg md:text-xl text-slate-600 mb-8 leading-relaxed">
                Get found by local customers searching for your products and services. 
                We optimize your Google Business Profile and local SEO for maximum visibility.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button className="bg-green-600 text-white hover:bg-green-700 px-8 py-6 rounded-full text-base font-semibold">
                    Get Started
                    <ArrowRight className="ml-2" size={18} />
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
                Why Google Business Profile Matters
              </h2>
              <p className="text-slate-600 mb-8">
                When customers search for businesses like yours, your Google Business Profile is often the first thing they see. 
                A well-optimized profile can significantly increase your visibility and drive more customers to your business.
              </p>
              <ul className="space-y-4">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-center">
                    <CheckCircle2 className="w-6 h-6 text-green-500 mr-3" />
                    <span className="text-lg text-slate-700">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-gradient-to-br from-green-500 to-blue-500 rounded-3xl p-8 text-white">
              <h3 className="font-heading text-2xl font-bold mb-6">Local Search Stats</h3>
              <div className="space-y-6">
                <div>
                  <div className="text-4xl font-bold mb-1">46%</div>
                  <div className="text-green-100">of all Google searches have local intent</div>
                </div>
                <div>
                  <div className="text-4xl font-bold mb-1">88%</div>
                  <div className="text-green-100">of mobile searches for local businesses result in a call or visit within 24 hours</div>
                </div>
                <div>
                  <div className="text-4xl font-bold mb-1">76%</div>
                  <div className="text-green-100">of people who search for something nearby visit a business within a day</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-center mb-12">
            Our Services
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200"
              >
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-4">
                  <service.icon className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="font-heading text-lg font-bold mb-3">{service.title}</h3>
                <ul className="space-y-2">
                  {service.items.map((item, idx) => (
                    <li key={idx} className="flex items-start text-sm">
                      <CheckCircle2 className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-slate-600">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-center mb-12">
            Why Choose Us?
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyChooseUs.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl p-6 shadow-md text-center border border-slate-200"
              >
                <item.icon className="w-12 h-12 text-green-600 mx-auto mb-4" />
                <h3 className="font-heading font-bold text-lg mb-2">{item.title}</h3>
                <p className="text-slate-600 text-sm">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-r from-green-600 to-blue-600">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Dominate Local Search?
          </h2>
          <p className="text-green-100 text-lg mb-8 max-w-2xl mx-auto">
            Let us optimize your Google Business Profile and local SEO strategy for maximum visibility.
          </p>
          <Link to="/contact">
            <Button className="bg-white text-green-600 hover:bg-slate-100 px-8 py-6 rounded-full text-base font-semibold">
              Get Free Consultation
              <ArrowRight className="ml-2" size={18} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default LocalSEO;
